import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import AdGrid from '../components/AdGrid';
import AdDetailPanel from '../components/AdDetailPanel';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { searchAds, updateAd, deleteAd } = useData();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
  }, [searchParams]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const data = await searchAds(searchQuery);
      setResults(data);
    } catch (err) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
      performSearch(query);
    }
  };

  const handleAdClick = (ad) => {
    setSelectedAd(ad);
    setIsPanelOpen(true);
  };

  const handleToggleFavorite = async (ad) => {
    try {
      await updateAd(ad.id, { is_favorite: !ad.is_favorite });
      setResults(results.map(a => a.id === ad.id ? { ...a, is_favorite: !a.is_favorite } : a));
      toast.success(ad.is_favorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (ad) => {
    if (window.confirm('Delete this ad?')) {
      try {
        await deleteAd(ad.id);
        setResults(results.filter(a => a.id !== ad.id));
        toast.success('Ad deleted');
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen" data-testid="search-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-6">
          <Search className="w-7 h-7 text-violet-400" />
          <h1 className="text-2xl md:text-3xl font-bold text-white">Search</h1>
        </div>

        <form onSubmit={handleSearch} className="relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <Input
            data-testid="search-input"
            type="text"
            placeholder="Search ads... try 'modern luxury real estate' or 'ugc skincare'"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-12 pr-12 h-14 text-lg bg-zinc-800/50 border-zinc-700 text-white placeholder:text-zinc-500"
          />
          {query && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </form>

        <p className="mt-3 text-sm text-zinc-500">
          Search across titles, notes, AI descriptions, tags, and OCR text
        </p>
      </div>

      {/* Results */}
      {searchParams.get('q') && (
        <div className="mb-6">
          <p className="text-zinc-400">
            {loading ? 'Searching...' : `${results.length} results for "${searchParams.get('q')}"`}
          </p>
        </div>
      )}

      <AdGrid
        ads={results}
        loading={loading}
        onAdClick={handleAdClick}
        onToggleFavorite={handleToggleFavorite}
        onDelete={handleDelete}
      />

      <AdDetailPanel
        ad={selectedAd}
        open={isPanelOpen}
        onClose={() => {
          setIsPanelOpen(false);
          setSelectedAd(null);
        }}
      />
    </div>
  );
};

export default SearchPage;
